import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import {
  ArrowBack,
  ArrowForward,
  CancelOutlined,
  CheckCircleOutline,
  DescriptionOutlined,
  FileDownloadOutlined,
  FileUploadOutlined,
  Replay,
  Search,
  StorageOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { globalTextFieldSx, sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import {
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { tooltipStyleProps } from "services/constants/Styles";
import { WizardStepper } from "pages/PayRoll/PayInputs/Shared/WizardStepper";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { readSheet } from "pages/PayRoll/Statutory/PF/PfUanImport";
import { MONTHS, emptySlab, slabCheck, sortSlabs } from "./ptConfigData";
import { checkPtSlabs, importPtSlabs } from "./ptConfigApi";
import { usePayrollSettingsCrumbs } from "../../payrollSettingsMenu";

// PT Configuration > site > Import slabs: Upload File -> Map Columns ->
// Preview & Import, the same layout as the ESI IP and PF UAN imports. The
// file is read and mapped in the browser; Map Columns > Next checks every
// row in the browser and sends the mapped rows to POST config/slabs/import/
// (nothing saved), so the server's row errors show on the red rows. Error
// rows open an Edit panel to correct and re-verify. When every row passes,
// the whole table is checked for gaps and overlaps (POST config/slabs/check/).
// Import puts the slabs into the version form; they are saved with the
// version (Save), like slabs added by hand.

const STEPS = ["Upload File", "Map Columns", "Preview & Import"];
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_ROWS = 200;
const APPLIES = { all: "ALL", women: "WOMEN", men: "MEN" };
const APPLIES_LABEL = { ALL: "All", WOMEN: "Women", MEN: "Men" };

const fieldsFor = (specialOn) => [
  { key: "wage_from", label: "Wage from", required: true, aliases: ["wage from", "from", "wage_from", "min wage", "from wage"] },
  { key: "wage_to", label: "Wage to", aliases: ["wage to", "to", "wage_to", "max wage", "to wage"] },
  { key: "amount", label: "PT amount", required: true, aliases: ["pt amount", "amount", "pt", "tax"] },
  { key: "applies_to", label: "Applies to", aliases: ["applies to", "applies_to", "gender", "for"] },
  { key: "special_month_amount", label: "Special month amount", required: specialOn, aliases: ["special month amount", "special_month_amount", "special amount", "february amount"] },
];

const autoMap = (headers, fields) => {
  const mapping = {};
  const used = new Set();
  headers.forEach((header, index) => {
    const name = String(header || "").trim().toLowerCase().replace(/[()₹*]/g, "").replace(/\s+/g, " ").trim();
    const field = fields.find((item) => !used.has(item.key) && (item.label.toLowerCase() === name || item.aliases.includes(name)));
    if (field) {
      mapping[index] = field.key;
      used.add(field.key);
    }
  });
  return mapping;
};
const missingFields = (mapping, fields) => {
  const mapped = new Set(Object.values(mapping));
  return fields.filter((field) => field.required && !mapped.has(field.key));
};

const GUIDELINES = [
  "Use the provided template. Columns can be in any order and renamed — check the matches in the Map Columns step.",
  "One row per slab. Wage from and Wage to are whole rupees and both ends are included.",
  "Leave Wage to blank only for the top slab (and above).",
  "Applies to is All, Women or Men (blank = All). A site needs All slabs, or both Women and Men slabs.",
  "Special month amount is needed only when the site has a different amount in one month.",
  "The imported slabs replace all slabs of this version. Nothing is saved until you save the version. Maximum 200 rows and 5 MB per file.",
];

const fieldSx = {
  ...globalTextFieldSx,
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: designColors.teal },
  "& .MuiInputBase-input": { fontSize: 14 },
};
const tableHeadSx = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, py: 1 };
const errorText = "#DC2626";

const formatBytes = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const textOf = (value) => (value === null || value === undefined ? "" : String(value).trim());
const shown = (value) => textOf(value) || "–";
// "21,000" / "₹21000" / "21000.00" -> "21000"
const rupees = (value) => {
  const clean = textOf(value).replace(/[₹,\s]/g, "");
  return /^\d+(\.0+)?$/.test(clean) ? String(Number(clean)) : clean;
};
const isWhole = (value) => /^\d+$/.test(value);

// ---- Row checks ----

const rawFromRow = (cells, mapping, fields) => {
  const raw = Object.fromEntries(fields.map((field) => [field.key, ""]));
  Object.entries(mapping).forEach(([index, key]) => {
    raw[key] = textOf(cells[index]);
  });
  return raw;
};

// Browser check of one row -> { rowNumber, raw, errors, body } (body only when it passed).
const checkRow = (rowNumber, raw, specialOn) => {
  const errors = [];
  const from = rupees(raw.wage_from);
  const to = rupees(raw.wage_to);
  const amount = rupees(raw.amount);
  const special = rupees(raw.special_month_amount);
  const applies = APPLIES[textOf(raw.applies_to).toLowerCase() || "all"];
  if (!from) errors.push("Wage from is required.");
  else if (!isWhole(from)) errors.push("Wage from must be whole rupees, 0 or more.");
  if (to && !isWhole(to)) errors.push("Wage to must be whole rupees, or blank for the top slab.");
  else if (to && isWhole(from) && Number(to) < Number(from)) errors.push("Wage to can't be below Wage from.");
  if (!amount) errors.push("PT amount is required.");
  else if (!isWhole(amount)) errors.push("PT amount must be whole rupees, 0 or more.");
  if (!applies) errors.push("Applies to must be All, Women or Men.");
  if (specialOn && !special) errors.push("Special month amount is required for this site.");
  else if (specialOn && !isWhole(special)) errors.push("Special month amount must be whole rupees.");
  return {
    rowNumber,
    raw,
    errors,
    body: errors.length ? null : { wage_from: from, wage_to: to, amount, applies_to: applies, special_month_amount: specialOn ? special : "" },
  };
};

// The rows that passed, as the server's import file (standard headers).
const csvOf = (items) => {
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const lines = [
    ["Wage from", "Wage to", "PT amount", "Applies to", "Special month amount"],
    ...items.map((item) => [item.body.wage_from, item.body.wage_to, item.body.amount, APPLIES_LABEL[item.body.applies_to], item.body.special_month_amount]),
  ];
  return new File([lines.map((line) => line.map(escape).join(",")).join("\n")], "slabs.csv", { type: "text/csv" });
};

// Sends the rows that passed the browser check to the server's import (it
// reads the file and saves nothing) and puts its row errors back. The
// server counts rows like a sheet: the first data row is row 2.
const serverCheck = async ({ domain, companyId, checked }) => {
  const sent = checked.filter((item) => item.body);
  if (!sent.length) return checked;
  const answer = await importPtSlabs({ domain, companyId, file: csvOf(sent) });
  const errorsByRow = {};
  answer.rowErrors.forEach((item) => {
    const target = sent[Number(item.row) - 2];
    if (target) (errorsByRow[target.rowNumber] = errorsByRow[target.rowNumber] || []).push(item.message || "Rejected by the server.");
  });
  const general = answer.rowErrors.filter((item) => !sent[Number(item.row) - 2]).map((item) => item.message);
  return checked.map((item) => {
    if (errorsByRow[item.rowNumber]) return { ...item, errors: errorsByRow[item.rowNumber], body: null };
    // Errors without a row of their own go on the first row sent.
    if (general.length && item === sent[0]) return { ...item, errors: general, body: null };
    return item;
  });
};

// Slab-table problems pinned to the rows involved: each Applies-to set must
// start at ₹0, run with no gaps or overlaps and end with one open top slab.
// Only rows that passed their own check take part. -> { rowNumber: [messages] }
const rs = (value) => `₹${Number(value).toLocaleString("en-IN")}`;
const tableProblems = (results) => {
  const problems = {};
  const add = (item, message) => (problems[item.rowNumber] = problems[item.rowNumber] || []).push(message);
  ["ALL", "WOMEN", "MEN"].forEach((group) => {
    const label = APPLIES_LABEL[group];
    const rows = results
      .filter((item) => item.body && item.body.applies_to === group)
      .sort((a, b) => Number(a.body.wage_from) - Number(b.body.wage_from) || a.rowNumber - b.rowNumber);
    rows.forEach((item, index) => {
      const from = Number(item.body.wage_from);
      const open = item.body.wage_to === "";
      const to = Number(item.body.wage_to);
      const next = rows[index + 1];
      if (index === 0 && from !== 0) add(item, `The first ${label} slab must start at ₹0 (this one starts at ${rs(from)}).`);
      if (!next) {
        if (!open) add(item, `This is the top ${label} slab, so leave Wage to blank (and above).`);
        return;
      }
      if (open) {
        add(item, `Only the top ${label} slab can leave Wage to blank — row ${next.rowNumber} starts above it.`);
        return;
      }
      const nextFrom = Number(next.body.wage_from);
      if (nextFrom > to + 1) {
        const message = `Gap: no ${label} slab covers ${rs(to + 1)} – ${rs(nextFrom - 1)} (between rows ${item.rowNumber} and ${next.rowNumber}).`;
        add(item, message);
        add(next, message);
      } else if (nextFrom <= to) {
        const message = `Overlap: rows ${item.rowNumber} and ${next.rowNumber} both cover ${rs(nextFrom)}${to > nextFrom ? ` – ${rs(to)}` : ""}.`;
        add(item, message);
        add(next, message);
      }
    });
  });
  return problems;
};
const withTableProblems = (results) => {
  const problems = tableProblems(results);
  return results.map((item) => ({ ...item, tableErrors: problems[item.rowNumber] || [] }));
};
const hasError = (result) => !result.body || result.tableErrors?.length > 0;

// ---- Shared bits (same as the ESI / PF imports) ----

const Card = ({ title, helper, action, children, sx }) => (
  <Mui.Box sx={{ ...detailCardSx, ...sx }}>
    {title ? (
      <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Mui.Box>
          <Mui.Typography sx={designType.sectionHeading}>{title}</Mui.Typography>
          {helper ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>{helper}</Mui.Typography> : null}
        </Mui.Box>
        {action}
      </Mui.Stack>
    ) : null}
    {children}
  </Mui.Box>
);

const FileCard = ({ file, onChange }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ ...detailCardSx, backgroundColor: designColors.slateBg }}>
    <Mui.Box sx={{ width: 40, height: 40, borderRadius: designRadii.iconButton, backgroundColor: "#E7F8EF", color: "#12B76A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <DescriptionOutlined />
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
      <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }} noWrap>{file.name}</Mui.Typography>
      <Mui.Typography sx={designType.captionText}>{file.rows.length.toLocaleString("en-IN")} rows · {formatBytes(file.size)}</Mui.Typography>
    </Mui.Box>
    {onChange ? <SecondaryButton onClick={onChange} sx={{ minHeight: 36, minWidth: 0 }}>Change File</SecondaryButton> : null}
  </Mui.Stack>
);

const TabButtons = ({ tabs, tab, counts, onChange }) => (
  <Mui.Stack direction="row" spacing={1} sx={{ overflowX: "auto" }} role="tablist">
    {tabs.map((item) => {
      const active = item.key === tab;
      return (
        <Mui.ButtonBase
          key={item.key}
          role="tab"
          aria-selected={active}
          onClick={() => onChange(item.key)}
          sx={{ px: 1.5, py: 0.75, flexShrink: 0, borderRadius: designRadii.control, border: `1px solid ${active ? designColors.tealDark : designColors.slate200}`, backgroundColor: active ? designColors.tealTint : designColors.white }}
        >
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: active ? designWeights.semibold : designWeights.medium, color: active ? designColors.tealDark : designColors.slate700, whiteSpace: "nowrap" }}>
            {item.label} ({counts[item.key].toLocaleString("en-IN")})
          </Mui.Typography>
        </Mui.ButtonBase>
      );
    })}
  </Mui.Stack>
);

const usePaged = (items, resetKey) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  useEffect(() => setPage(0), [resetKey]);
  return {
    pageItems: items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    footer: (
      <Mui.Box sx={{ px: 2, py: 1, borderTop: `1px solid ${designColors.slate200}` }}>
        {paginationFooter({
          page,
          rowsPerPage,
          total: items.length,
          itemLabel: "rows",
          onPageChange: setPage,
          onRowsPerPageChange: (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          },
          rowsPerPageOptions,
        })}
      </Mui.Box>
    ),
  };
};

// ---- Step 1: Upload ----

const UploadStep = ({ file, error, onFile, onTemplate }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pick = (files) => {
    if (files?.[0]) onFile(files[0]);
  };

  return (
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 1.5, alignItems: "stretch" }}>
      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <Mui.Box
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            pick(event.dataTransfer.files);
          }}
          sx={{
            ...detailCardSx,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 3,
            textAlign: "center",
            cursor: "pointer",
            borderStyle: "dashed",
            borderWidth: 2,
            borderColor: dragging ? designColors.teal : designColors.slate300,
            backgroundColor: dragging ? designColors.tealTint : designColors.white,
            "&:hover": { borderColor: designColors.teal },
          }}
        >
          <input
            ref={inputRef}
            hidden
            type="file"
            accept=".xlsx,.xls,.csv"
            data-testid="pt-slab-import-input"
            onChange={(event) => {
              pick(event.target.files);
              event.target.value = "";
            }}
          />
          <FileUploadOutlined sx={{ fontSize: 40, color: designColors.teal }} />
          <Mui.Typography sx={{ ...designType.sectionHeading, fontSize: 16, mt: 1.5 }}>Drag and drop your file here</Mui.Typography>
          <Mui.Typography sx={{ ...designType.helperText, mt: 0.5 }}>
            or <Mui.Box component="span" sx={{ color: designColors.tealDark, fontWeight: designWeights.semibold }}>click to browse</Mui.Box>
          </Mui.Typography>
          <Mui.Typography sx={{ ...designType.captionText, mt: 2 }}>Supported formats: Excel (.xlsx, .xls), CSV (.csv) · Max 5 MB</Mui.Typography>
          <Mui.Button
            startIcon={<FileDownloadOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              onTemplate();
            }}
            sx={{ mt: 2, textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}
          >
            Download PT Slab Template
          </Mui.Button>
        </Mui.Box>
        {file ? <FileCard file={file} /> : null}
        {error ? <RequestInfoBanner tone="danger" title="Cannot use this file" body={error} /> : null}
      </Mui.Stack>

      <Mui.Stack spacing={1.5} sx={{ minHeight: "100%" }}>
        <RequestInfoBanner title="Template Information" body="Use the PT slab template to enter the slabs published by your state or local body. The template has the columns and this version's current slabs as example rows." />
        <Card sx={{ flex: 1 }}>
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <DescriptionOutlined sx={{ fontSize: 20, color: designColors.tealDark }} />
            <Mui.Typography sx={designType.sectionHeading}>PT Slab Import Guidelines</Mui.Typography>
          </Mui.Stack>
          <Mui.Stack spacing={1}>
            {GUIDELINES.map((line, index) => (
              <Mui.Stack key={line} direction="row" spacing={1.25} alignItems="flex-start">
                <Mui.Box sx={{ ...designType.captionText, flexShrink: 0, width: 20, height: 20, borderRadius: "50%", backgroundColor: designColors.slate100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {index + 1}
                </Mui.Box>
                <Mui.Typography sx={designType.helperText}>{line}</Mui.Typography>
              </Mui.Stack>
            ))}
          </Mui.Stack>
        </Card>
      </Mui.Stack>
    </Mui.Box>
  );
};

// ---- Step 2: Map columns ----

const MapStep = ({ file, fields, mapping, onMappingChange, onReset, onChangeFile }) => {
  const missing = missingFields(mapping, fields);
  const usedFields = new Set(Object.values(mapping));
  return (
    <Mui.Stack spacing={1.5}>
      <FileCard file={file} onChange={onChangeFile} />
      <Card
        title="Map File Columns"
        helper="Columns were matched automatically by header name — they can be in any order and renamed. Check each match and change it if needed."
        action={
          <Mui.Button startIcon={<Replay />} onClick={onReset} sx={{ textTransform: "none", fontWeight: designWeights.semibold, color: designColors.tealDark }}>
            Reset Mapping
          </Mui.Button>
        }
      >
        <Mui.TableContainer sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>File Column (from Excel)</Mui.TableCell>
                <Mui.TableCell sx={tableHeadSx}>Map to System Field</Mui.TableCell>
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {file.headers.map((header, index) => {
                const fieldKey = mapping[index] ?? "";
                return (
                  <Mui.TableRow key={`${header}-${index}`}>
                    <Mui.TableCell sx={{ ...cellSx, width: "45%" }}>{header || `Column ${index + 1}`}</Mui.TableCell>
                    <Mui.TableCell sx={cellSx}>
                      {/* A field already mapped to another column is left out. */}
                      <SelectField
                        value={fieldKey}
                        onChange={(value) => onMappingChange(index, value)}
                        options={[
                          { value: "", label: "Do not import" },
                          ...fields
                            .filter((option) => !usedFields.has(option.key) || option.key === fieldKey)
                            .map((option) => ({ value: option.key, label: `${option.label}${option.required ? " *" : ""}` })),
                        ]}
                        placeholder="Do not import"
                      />
                    </Mui.TableCell>
                  </Mui.TableRow>
                );
              })}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {missing.length ? (
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner tone="warning" title="Required fields not mapped" body={`Map a column to: ${missing.map((item) => item.label).join(", ")}.`} />
          </Mui.Box>
        ) : null}
      </Card>
    </Mui.Stack>
  );
};

// ---- Step 3: Preview & Import ----

const PREVIEW_TABS = [
  { key: "all", label: "All Rows" },
  { key: "valid", label: "Valid Rows" },
  { key: "error", label: "Error Rows" },
];

// Error rows (their own errors or a gap / overlap) are tinted red. Every row
// opens the Edit panel, which lists the row's errors (the table shows only
// the values, same as ESI / PF).
const errorRowSx = {
  cursor: "pointer",
  backgroundColor: "#FEF2F2",
  "&:hover": { backgroundColor: "#FEE2E2" },
  "&:focus-visible": { outline: `2px solid ${errorText}`, outlineOffset: -2 },
};
const validRowSx = {
  cursor: "pointer",
  "&:hover": { backgroundColor: designColors.slateBg },
  "&:focus-visible": { outline: `2px solid ${designColors.teal}`, outlineOffset: -2 },
};

const cellValue = (result, key) => {
  if (!result.body) return shown(result.raw[key]);
  if (key === "wage_to") return result.body.wage_to ? Number(result.body.wage_to).toLocaleString("en-IN") : "and above";
  if (key === "applies_to") return APPLIES_LABEL[result.body.applies_to];
  const value = result.body[key];
  return value === "" || value == null ? "–" : Number(value).toLocaleString("en-IN");
};

const PreviewStep = ({ results, fields, tableCheck, versionName, onFixRow }) => {
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const valid = results.filter((result) => !hasError(result));
  const invalid = results.filter(hasError);
  const counts = { all: results.length, valid: valid.length, error: invalid.length };

  const rows = useMemo(() => {
    const base = tab === "valid" ? valid : tab === "error" ? invalid : results;
    const needle = query.trim().toLowerCase();
    return needle ? base.filter((result) => Object.values(result.raw).some((value) => textOf(value).toLowerCase().includes(needle))) : base;
  }, [tab, query, results, valid, invalid]);
  const { pageItems, footer } = usePaged(rows, `${tab}:${query}`);

  return (
    <Mui.Stack spacing={1.5}>
      <Card title="Summary" helper={`The imported slabs replace every slab of ${versionName}. Nothing is saved until you save the version.`}>
        <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
          <StatCard icon={StorageOutlined} title="Total Rows" value={counts.all.toLocaleString("en-IN")} accent="#12B76A" tint="#E7F8EF" onClick={() => setTab("all")} isActive={tab === "all"} activeBorder />
          <StatCard icon={CheckCircleOutline} title="Valid Rows" value={counts.valid.toLocaleString("en-IN")} accent={designColors.tealDark} tint={designColors.tealTint} onClick={() => setTab("valid")} isActive={tab === "valid"} activeBorder />
          <StatCard icon={CancelOutlined} title="Error Rows" value={counts.error.toLocaleString("en-IN")} accent={errorText} tint="#FEF2F2" onClick={() => setTab("error")} isActive={tab === "error"} activeBorder />
        </Mui.Box>
        <Mui.Box sx={{ mt: 1.5 }}>
          {invalid.length ? (
            <RequestInfoBanner
              tone="warning"
              body={`${invalid.length.toLocaleString("en-IN")} row(s) have errors. Slabs must cover every wage with no gaps, so all rows must pass before importing — click a red row to correct it.`}
            />
          ) : tableCheck.loading ? (
            <RequestInfoBanner body="Checking the slab table for gaps and overlaps…" />
          ) : tableCheck.message ? (
            <RequestInfoBanner tone={tableCheck.valid ? "info" : "danger"} title={tableCheck.valid ? "Slab table check passed" : "Slab table problem"} body={tableCheck.message} />
          ) : null}
        </Mui.Box>
      </Card>

      <Mui.Box sx={{ ...detailCardSx, p: 0, overflow: "hidden" }}>
        <Mui.Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 2 }}>
          <TabButtons tabs={PREVIEW_TABS} tab={tab} counts={counts} onChange={setTab} />
          <Mui.TextField
            size="small"
            placeholder="Search..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ ...fieldSx, width: { xs: "100%", md: 260 } }}
            InputProps={{ startAdornment: <Mui.InputAdornment position="start"><Search sx={{ fontSize: 18, color: designColors.slate400 }} /></Mui.InputAdornment> }}
          />
        </Mui.Stack>
        <Mui.TableContainer sx={{ overflowX: "auto" }}>
          <Mui.Table size="small">
            <Mui.TableHead>
              <Mui.TableRow>
                <Mui.TableCell sx={tableHeadSx}>Row</Mui.TableCell>
                {fields.map((field) => <Mui.TableCell key={field.key} sx={tableHeadSx}>{field.label}</Mui.TableCell>)}
              </Mui.TableRow>
            </Mui.TableHead>
            <Mui.TableBody>
              {pageItems.length === 0 ? (
                <Mui.TableRow>
                  <Mui.TableCell colSpan={fields.length + 1} sx={{ ...cellSx, textAlign: "center", py: 4 }}>No rows to show.</Mui.TableCell>
                </Mui.TableRow>
              ) : (
                pageItems.map((result) => (
                  <Mui.TableRow
                    key={result.rowNumber}
                    sx={hasError(result) ? errorRowSx : validRowSx}
                    tabIndex={0}
                    title={hasError(result) ? "Click to correct this row" : "Click to edit this row"}
                    onClick={() => onFixRow(result.rowNumber)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onFixRow(result.rowNumber);
                      }
                    }}
                  >
                    <Mui.TableCell sx={cellSx}>{result.rowNumber}</Mui.TableCell>
                    {fields.map((field) => (
                      <Mui.TableCell key={field.key} sx={cellSx}>{cellValue(result, field.key)}</Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                ))
              )}
            </Mui.TableBody>
          </Mui.Table>
        </Mui.TableContainer>
        {footer}
      </Mui.Box>
    </Mui.Stack>
  );
};

// ---- Edit an error row (opened from the preview) ----

const APPLIES_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Women", label: "Women" },
  { value: "Men", label: "Men" },
];

const FIX_FIELDS = [
  { key: "wage_from", label: "Wage from (₹) *", helper: "Whole rupees, 0 or more.", placeholder: "e.g. 21001" },
  { key: "wage_to", label: "Wage to (₹)", helper: "Blank only for the top slab (and above).", placeholder: "and above" },
  { key: "amount", label: "PT amount (₹) *", helper: "Whole rupees, 0 or more.", placeholder: "e.g. 135" },
  { key: "applies_to", label: "Applies to", helper: "All, Women or Men.", placeholder: "All" },
  { key: "special_month_amount", label: "Special month amount (₹)", helper: "Only for a site with a different amount in one month.", placeholder: "e.g. 300" },
];

// Side panel over the import: the row's errors on top, its values below to
// correct, then Re-Verify runs the browser check and the server's check for
// this row. A row that passes turns valid and the panel closes.
const RowFixPanel = ({ row, fields, onClose, onVerify }) => {
  const mappedKeys = new Set(fields.map((field) => field.key));
  const fixFields = FIX_FIELDS.filter((item) => mappedKeys.has(item.key));
  const [form, setForm] = useState(() => ({ ...row.raw }));
  const [errors, setErrors] = useState(() => [...row.errors, ...(row.tableErrors || [])]);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  const verify = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = await onVerify(row.rowNumber, form);
      if (!next) return;
      const all = [...next.errors, ...(next.tableErrors || [])];
      if (next.body && !all.length) onClose();
      else setErrors(all);
    } catch (error) {
      setErrors([`Could not check this row: ${error.message || "please try again."}`]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlobalSidePanel
      open
      onClose={busy ? undefined : onClose}
      hideExpand
      title="Edit Slab Row"
      subtitle={`Row ${row.rowNumber}. ${errors.length ? "Correct the values and re-verify." : "Edit the values and re-verify."}`}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel={busy ? "Verifying..." : "Re-Verify"} primaryProps={{ disabled: busy, loading: busy, onClick: verify }} />}
    >
      <Mui.Stack spacing={2}>
        {errors.length ? (
          <RequestInfoBanner
            tone="danger"
            title={errors.length > 1 ? `${errors.length} errors in this row` : "Error in this row"}
            body={errors.map((message) => (
              <Mui.Box key={message} component="span" sx={{ display: "block" }}>• {message}</Mui.Box>
            ))}
          />
        ) : (
          <RequestInfoBanner body="No errors in this row. Change the values and re-verify if needed." />
        )}
        <FormSection title="Slab" info="Values from the file. They go into the version form on import.">
          {fixFields.map((item, index) => (
            <Field key={item.key} label={item.label} helper={item.helper} wide={index === fixFields.length - 1 && fixFields.length % 2 === 1}>
              {item.key === "applies_to" ? (
                <SelectField
                  value={APPLIES_LABEL[APPLIES[textOf(form.applies_to).toLowerCase()]] || form.applies_to}
                  onChange={set("applies_to")}
                  options={APPLIES_OPTIONS}
                  placeholder={item.placeholder}
                />
              ) : (
                <TextField value={form[item.key]} onChange={set(item.key)} placeholder={item.placeholder} />
              )}
            </Field>
          ))}
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---- Wizard ----

// onImport(slabs): the slabs that replace the version's slabs.
export const PtSlabImport = ({ open, domain, companyId, form, siteName, onClose, onImport }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const specialOn = Boolean(form?.special_month_on);
  const fields = useMemo(() => fieldsFor(specialOn), [specialOn]);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [mapping, setMapping] = useState({});
  const [results, setResults] = useState([]);
  const [validating, setValidating] = useState(false);
  const [validateError, setValidateError] = useState("");
  const [tableCheck, setTableCheck] = useState({ loading: false, valid: false, message: "" });
  const [fixRowNumber, setFixRowNumber] = useState(null);
  // Results plus the gap / overlap messages pinned to their rows.
  const checkedRows = useMemo(() => withTableProblems(results), [results]);
  const tableRowProblems = checkedRows.some((item) => item.tableErrors.length);
  const fixRow = fixRowNumber == null ? null : checkedRows.find((item) => item.rowNumber === fixRowNumber) || null;

  useEffect(() => {
    if (!open) return;
    setStep(0); setFile(null); setFileError(""); setMapping({});
    setResults([]); setValidateError(""); setTableCheck({ loading: false, valid: false, message: "" }); setFixRowNumber(null);
  }, [open]);

  const mappedFields = useMemo(() => {
    const mapped = new Set(Object.values(mapping));
    return fields.filter((field) => mapped.has(field.key));
  }, [mapping, fields]);
  const allValid = results.length > 0 && results.every((item) => item.body);
  const slabs = useMemo(() => (allValid ? sortSlabs(results.map((item) => emptySlab(item.body))) : []), [allValid, results]);

  // Every row passed: check the whole table for gaps and overlaps (server;
  // the local check if the server can't be reached).
  const slabsKey = allValid ? JSON.stringify(slabs.map(({ id, ...rest }) => rest)) : "";
  useEffect(() => {
    if (!open || !slabsKey) {
      setTableCheck({ loading: false, valid: false, message: "" });
      return undefined;
    }
    const controller = new AbortController();
    setTableCheck({ loading: true, valid: false, message: "" });
    const list = JSON.parse(slabsKey);
    checkPtSlabs({ domain, companyId, slabs: list, specialMonth: specialOn ? form.special_month : "", signal: controller.signal })
      .then((data) => {
        const message = String(data?.message || "").trim().replace(/^✓\s*/, "");
        setTableCheck({ loading: false, valid: Boolean(data?.ok), message: message || slabCheck(list, specialOn).message });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        const local = slabCheck(list, specialOn);
        setTableCheck({ loading: false, valid: local.valid, message: local.message });
      });
    return () => controller.abort();
  }, [open, slabsKey, domain, companyId, specialOn, form?.special_month]);

  const handleFile = async (picked) => {
    setFileError("");
    const extension = picked.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      setFile(null);
      setFileError("Unsupported file type. Upload an Excel (.xlsx, .xls) or CSV (.csv) file.");
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFile(null);
      setFileError("The file is larger than 5 MB.");
      return;
    }
    try {
      const [headerRow = [], ...body] = await readSheet(picked);
      const headers = headerRow.map((cell) => String(cell ?? "").trim());
      const rows = body.filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""));
      if (!headers.some(Boolean) || !rows.length) {
        setFile(null);
        setFileError("The file has no slab rows under the header row.");
        return;
      }
      if (rows.length > MAX_ROWS) {
        setFile(null);
        setFileError(`The file has ${rows.length.toLocaleString("en-IN")} rows. A slab table can have at most 200.`);
        return;
      }
      setFile({ name: picked.name, size: picked.size, headers, rows });
      setMapping(autoMap(headers, fields));
    } catch (error) {
      setFile(null);
      setFileError(error.message);
    }
  };

  // Template: the columns plus this version's slabs as example rows.
  const downloadTemplate = () => {
    const examples = sortSlabs((form?.slabs || []).filter((slab) => slab.wage_from !== "" || slab.amount !== ""));
    const rows = examples.length
      ? examples.map((slab) => [slab.wage_from, slab.wage_to, slab.amount, APPLIES_LABEL[slab.applies_to] || "All", specialOn ? slab.special_month_amount : ""])
      : [
          ["0", "21000", "0", "All", ""],
          ["21001", "", "180", "All", ""],
        ];
    const sheet = XLSX.utils.aoa_to_sheet([fields.map((field) => field.label), ...rows]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "PT slabs");
    XLSX.writeFile(book, "PT Slab Import Template.xlsx");
  };

  // Map Columns > Next: browser check, then the server's check, then preview.
  const handleMapNext = async () => {
    const checked = file.rows.map((cells, index) => checkRow(index + 1, rawFromRow(cells, mapping, fields), specialOn));
    setValidating(true);
    setValidateError("");
    try {
      setResults(await serverCheck({ domain, companyId, checked }));
      setStep(2);
    } catch (error) {
      setValidateError(error.message || "Please try again.");
    } finally {
      setValidating(false);
    }
  };

  // Edit panel > Re-Verify: browser check, then the server's check for this row.
  // The answer carries this row's gap / overlap messages against the other rows.
  const verifyRow = async (rowNumber, raw) => {
    let next = checkRow(rowNumber, raw, specialOn);
    if (next.body) next = (await serverCheck({ domain, companyId, checked: [next] }))[0];
    const updated = results.map((item) => (item.rowNumber === rowNumber ? next : item));
    setResults(updated);
    return withTableProblems(updated).find((item) => item.rowNumber === rowNumber);
  };

  const canImport = allValid && !tableRowProblems && !tableCheck.loading && tableCheck.valid;
  const runImport = () => {
    if (!canImport) return;
    onImport(slabs);
    onClose();
  };

  const canContinue = (step === 0 && Boolean(file)) || (step === 1 && missingFields(mapping, fields).length === 0);
  const importHint = !allValid || tableRowProblems ? "Correct the red rows first." : tableCheck.loading ? "Checking the slab table…" : !tableCheck.valid ? "Fix the slab table problem first." : "";

  const footer = (
    <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ width: "100%" }}>
      <SecondaryButton disabled={validating} onClick={onClose}>Cancel</SecondaryButton>
      <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5}>
        {step > 0 ? <SecondaryButton disabled={validating} startIcon={<ArrowBack />} onClick={() => setStep(step - 1)}>Back</SecondaryButton> : null}
        {step < 2 ? (
          <PrimaryButton endIcon={<ArrowForward />} disabled={!canContinue || validating} loading={step === 1 && validating} onClick={step === 1 ? handleMapNext : () => setStep(step + 1)}>
            {step === 1 && validating ? "Validating..." : "Next"}
          </PrimaryButton>
        ) : (
          <Mui.Tooltip arrow placement="top" slotProps={tooltipStyleProps} title={importHint}>
            <span>
              <PrimaryButton startIcon={<FileUploadOutlined />} disabled={!canImport} onClick={runImport}>
                Import {allValid ? slabs.length.toLocaleString("en-IN") : ""} slabs
              </PrimaryButton>
            </span>
          </Mui.Tooltip>
        )}
      </Mui.Stack>
    </Mui.Stack>
  );

  const subtitles = [
    `Upload a file with the PT slabs for ${form?.name || "this version"}.`,
    "Map your file columns to the slab fields.",
    "Review the slabs and import.",
  ];

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={open}
      onToggleExpand={() => {}}
      hideExpand
      title="Import PT Slabs"
      subtitle={subtitles[step]}
      // Payroll -> Payroll home; Professional Tax / site -> back to the site (closes this panel).
      breadcrumb={[
        ...settingsCrumbs("Statutory"),
        { label: "Professional Tax", onClick: onClose },
        ...(siteName ? [{ label: siteName, onClick: onClose }] : []),
        "Import Slabs",
      ]}
      bodySx={{ backgroundColor: designColors.pageBg, p: { xs: 1.5, sm: 2 } }}
      footer={footer}
    >
      <Mui.Stack spacing={1.5}>
        <Mui.Box sx={{ ...detailCardSx, py: 1 }}>
          <WizardStepper steps={STEPS} step={step} />
        </Mui.Box>
        {step === 0 ? (
          <UploadStep file={file} error={fileError} onFile={handleFile} onTemplate={downloadTemplate} />
        ) : step === 1 ? (
          <MapStep
            file={file}
            fields={fields}
            mapping={mapping}
            onMappingChange={(index, fieldKey) =>
              setMapping((current) => {
                const next = { ...current };
                if (fieldKey) next[index] = fieldKey;
                else delete next[index];
                return next;
              })
            }
            onReset={() => setMapping(autoMap(file.headers, fields))}
            onChangeFile={() => setStep(0)}
          />
        ) : (
          <PreviewStep results={checkedRows} fields={mappedFields} tableCheck={tableCheck} versionName={form?.name || "this version"} onFixRow={setFixRowNumber} />
        )}
        {specialOn && step === 0 ? (
          <RequestInfoBanner body={`This site has a different amount in ${MONTHS[Number(form.special_month) - 1] || "one month"}, so each row needs a Special month amount.`} />
        ) : null}
        {validateError ? <RequestInfoBanner tone="danger" title="Could not validate the rows" body={validateError} /> : null}
      </Mui.Stack>
      {fixRow && step === 2 ? <RowFixPanel key={fixRow.rowNumber} row={fixRow} fields={mappedFields} onClose={() => setFixRowNumber(null)} onVerify={verifyRow} /> : null}
    </GlobalSidePanel>
  );
};
